import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box, Typography, TextField, Button, Stack, Divider, CircularProgress, InputAdornment,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import api, { apiError, tokens } from "../api";

/*
 * Register and sign in, as a panel rather than a page.
 *
 * To the customer they are one act - prove the email, get in - and which of the two it
 * turns out to be is the server's business: it knows whether the address already has an
 * account and says so in `registered`.
 *
 * Lifted out of its own page so it can sit on the landing page itself. A separate sign-up
 * screen asked somebody who had just read what the product does to click once more and
 * land on an empty page before they could try it.
 *
 * The email cannot be edited once the code is sent. Changing it there would send the code
 * to one address and verify another, and the "wrong code" that follows reads as a broken
 * product rather than a typo.
 */

// MUI's outlined field is drawn for a light page; these are the colours it needs on a
// dark one. One object, so every field matches.
const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff",
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.18)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.35)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.55)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiFormHelperText-root": { color: "rgba(255,255,255,.45)" },
};

const primaryBtn = { borderRadius: 999, py: 1.2 };

export default function AuthPanel() {
    const [params] = useSearchParams();
    const navigate = useNavigate();

    // The plant puts the admin's email in the link, so the field is filled in and the
    // customer cannot accidentally register an address their SCADA does not know about.
    const expectedEmail = (params.get("email") || "").toLowerCase();
    const plantName = params.get("plant") || "";

    const [email, setEmail] = useState(expectedEmail);
    const [step, setStep] = useState("email");   // 'email' | 'code'
    const [registered, setRegistered] = useState(false);
    const [form, setForm] = useState({ code: "" });
    const [busy, setBusy] = useState(false);

    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

    const sendCode = async () => {
        setBusy(true);
        try {
            const res = await api.post("/auth/otp", { email });
            setRegistered(Boolean(res.data?.registered));
            setStep("code");
            toast.success(`Code sent to ${email}`);
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const verify = async () => {
        setBusy(true);
        try {
            const res = await api.post("/auth/verify-otp", { email, ...form });
            tokens.account.set(res.data.token);
            navigate("/account");
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const google = async (credential) => {
        setBusy(true);
        try {
            const res = await api.post("/auth/google", { credential });
            tokens.account.set(res.data.token);
            navigate("/account");
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    return (
        <Box sx={{
            p: { xs: 3, sm: 4 }, borderRadius: 4,
            bgcolor: "rgba(255,255,255,.05)",
            border: "1px solid rgba(255,255,255,.12)",
            boxShadow: "0 30px 90px rgba(0,0,0,.45)",
        }}>
            <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: -0.5 }}>
                {step === "email" ? "Get started" : registered ? "Welcome back" : "Create your account"}
            </Typography>
            <Typography sx={{ mt: 1, mb: 3, color: "rgba(255,255,255,.6)", fontSize: 15 }}>
                {step === "email"
                    ? "Enter your email and we will send you a code."
                    : <>We sent a code to <b style={{ color: "#fff" }}>{email}</b>.</>}
            </Typography>

            {/*
              * Shown before anything is typed, because an email that does not match the one
              * the SCADA admin was created with produces an account the plant can never find -
              * and nothing later in the flow would hint at why.
              */}
            {expectedEmail && step === "email" && (
                <Stack direction="row" spacing={1.2} sx={{
                    alignItems: "center", mb: 2.5, px: 1.8, py: 1.3, borderRadius: 2.5,
                    bgcolor: "rgba(46,125,32,.16)", border: "1px solid rgba(123,217,95,.35)",
                }}>
                    <InfoOutlinedIcon sx={{ fontSize: 19, color: "#7BD95F" }} />
                    <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,.85)" }}>
                        <b>{plantName || "Your plant"}</b> is linked to this email
                    </Typography>
                </Stack>
            )}

            {step === "email" ? (
                <Stack spacing={2.2}>
                    <TextField
                        label="Email" value={email} fullWidth sx={darkField}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && emailOk && sendCode()}
                        error={Boolean(email) && !emailOk}
                        helperText={email && !emailOk ? "That email does not look right" : " "}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <MailOutlineIcon sx={{ fontSize: 20, color: "rgba(255,255,255,.4)" }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Button variant="contained" size="large" disabled={!emailOk || busy} onClick={sendCode}
                        sx={primaryBtn}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                        Send code
                    </Button>

                    <Divider sx={{
                        color: "rgba(255,255,255,.4)", fontSize: 13,
                        "&::before, &::after": { borderColor: "rgba(255,255,255,.14)" },
                    }}>or</Divider>

                    {/* Google draws its own button and will not take our palette, so it is
                        asked for its dark pill rather than left white on a dark card. */}
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <GoogleLogin
                            onSuccess={(r) => google(r.credential)}
                            onError={() => toast.error("Google sign-in failed")}
                            theme="filled_black" shape="pill" width="330"
                        />
                    </Box>
                </Stack>
            ) : (
                <Stack spacing={2.2}>
                    <TextField
                        label="6-digit code" value={form.code} autoFocus fullWidth sx={darkField}
                        onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/\D/g, "") }))}
                        onKeyDown={(e) => e.key === "Enter" && form.code.length === 6 && verify()}
                        slotProps={{
                            htmlInput: {
                                maxLength: 6, inputMode: "numeric",
                                style: { letterSpacing: 12, fontSize: 24, fontWeight: 700 },
                            },
                        }}
                        helperText="Valid for 10 minutes"
                    />

                    {/* Nothing else is asked here. Name and company prove nothing about
                        this email, and a form between a correct code and the way in is a
                        form people abandon - the console asks for them on the profile,
                        once they are already inside. */}

                    <Button variant="contained" size="large" disabled={form.code.length !== 6 || busy}
                        onClick={verify} sx={primaryBtn}
                        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
                        {registered ? "Log in" : "Create account"}
                    </Button>

                    <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                        <Button size="small" startIcon={<ArrowBackIcon fontSize="small" />}
                            onClick={() => setStep("email")}
                            sx={{ color: "rgba(255,255,255,.7)" }}>Change email</Button>
                        <Button size="small" disabled={busy} onClick={sendCode}
                            sx={{ color: "rgba(255,255,255,.7)" }}>Resend</Button>
                    </Stack>
                </Stack>
            )}
        </Box>
    );
}
